import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import type { TextInput as NativeTextInput } from 'react-native';
import { TextInput, Button, Text, useTheme, Surface } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { InfoBox } from '@/src/components/InfoBox';
import { confirmPhoneVerificationCode } from './phoneVerificationService';
import type { ApiResponse } from '@/src/api/apiService';

interface PhoneVerificationConfirmScreenProps {
  /** Phone number being verified */
  phoneNumber: string;
  /** Callback when verification is successful */
  onVerificationSuccess?: () => void;
  /** Callback when user wants to change phone number */
  onChangePhone?: () => void;
  /** Maximum OTP length (default 6) */
  otpLength?: number;
  /** Initial resend cooldown in seconds */
  initialCooldown?: number;
  /** Test ID for testing */
  testID?: string;
}

export interface PhoneVerificationConfirmScreenRef {
  /** Clear the OTP input */
  clearOTP: () => void;
}

export const PhoneVerificationConfirmScreen: React.FC<PhoneVerificationConfirmScreenProps> = ({
  phoneNumber,
  onVerificationSuccess,
  onChangePhone,
  otpLength = 6,
  initialCooldown = 600,
  testID = 'phone-verification-confirm',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  
  type OtpInputRef = React.ComponentRef<typeof TextInput> & NativeTextInput;
  const otpInputRef = useRef<OtpInputRef | null>(null);

  // Format OTP as user types (digits only)
  const formatOTP = (value: string): string => {
    return value.replace(/\D/g, '').slice(0, otpLength);
  };

  const handleOTPChange = (text: string) => {
    const formatted = formatOTP(text);
    setOtp(formatted);
    setError(null);
  };

  // Seed resend cooldown on mount
  useEffect(() => {
    if (initialCooldown > 0) {
      setResendCooldown(initialCooldown);
    }
  }, [initialCooldown]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleVerify = useCallback(async () => {
    if (otp.length !== otpLength) {
      setError(t('enterFullCode', 'Please enter the complete {{length}}-digit code', { length: otpLength }));
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      const response: ApiResponse<{ message?: string; phone_number?: string }> = 
        await confirmPhoneVerificationCode(phoneNumber, otp);

      if (response.ok) {
        onVerificationSuccess?.();
      } else {
        const responseData = response.data as any;
        const errorDetail = responseData?.detail || 
                           responseData?.non_field_errors?.[0] ||
                           responseData?.code?.[0] ||
                           t('verificationFailed', 'Verification failed. Please check the code and try again.');
        setError(errorDetail);
        setAttempts((prev) => prev + 1);
        
        // Clear OTP on failed attempt
        setOtp('');
        
        // Focus the input again
        otpInputRef.current?.focus();
      }
    } catch (err) {
      setError(t('verificationError', 'An error occurred. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [otp, otpLength, phoneNumber, t, onVerificationSuccess]);

  const handleResend = useCallback(() => {
    // This would trigger a resend - parent should handle this via onChangePhone
    // or implement resend logic here
    onChangePhone?.();
  }, [onChangePhone]);

  // Mask phone number for display (show last 4 digits)
  const maskedPhone = phoneNumber.length > 4 
    ? `***${phoneNumber.slice(-4)}`
    : `***${phoneNumber}`;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]} testID={testID}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <Surface style={styles.content} elevation={1}>
          <Text variant="headlineSmall" style={styles.title}>
            {t('enterVerificationCode', 'Enter Verification Code')}
          </Text>
          
          <Text variant="bodyMedium" style={styles.description}>
            {t('codeSentToPhone', 'We sent a code to {{phone}}', { phone: maskedPhone })}
          </Text>

          <View style={styles.inputContainer}>
            <TextInput
              ref={otpInputRef}
              label={t('verificationCode', 'Verification Code')}
              mode="outlined"
              value={otp}
              onChangeText={handleOTPChange}
              keyboardType="number-pad"
              textContentType="oneTimeCode"
              autoComplete="one-time-code"
              disabled={isLoading}
              error={!!error}
              style={styles.otpInput}
              maxLength={otpLength}
              testID={`${testID}-otp-input`}
            />
            
            {error && (
              <Text variant="bodySmall" style={[styles.errorText, { color: theme.colors.error }]}>
                {error}
              </Text>
            )}
          </View>

          <Button
            mode="contained"
            onPress={handleVerify}
            loading={isLoading}
            disabled={isLoading || otp.length !== otpLength}
            style={styles.button}
            testID={`${testID}-verify-button`}
          >
            {t('verify', 'Verify')}
          </Button>

          <View style={styles.resendContainer}>
            <Text variant="bodySmall" style={styles.resendText}>
              {t('didNotReceiveCode', "Didn't receive the code?")}
            </Text>
            <Button
              mode="text"
              compact
              onPress={handleResend}
              disabled={resendCooldown > 0}
              testID={`${testID}-resend-button`}
            >
              {resendCooldown > 0 
                ? t('resendIn', 'Resend in {{seconds}}s', { seconds: resendCooldown })
                : t('resend', 'Resend')}
            </Button>
          </View>

          {attempts >= 3 && (
            <InfoBox
              variant="warning"
              title={t('havingTrouble', 'Having trouble?')}
              message={t('checkPhoneNumber', 'Make sure your phone number is correct and can receive SMS messages.')}
              testID={`${testID}-attempts-warning`}
            />
          )}
        </Surface>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    margin: 16,
    padding: 20,
    borderRadius: 12,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    textAlign: 'center',
    marginBottom: 24,
    opacity: 0.7,
  },
  inputContainer: {
    marginBottom: 16,
  },
  otpInput: {
    textAlign: 'center',
    fontSize: 24,
    letterSpacing: 8,
  },
  errorText: {
    marginTop: 4,
    marginLeft: 8,
    textAlign: 'center',
  },
  button: {
    marginTop: 8,
  },
  resendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  resendText: {
    marginRight: 4,
    opacity: 0.7,
  },
});

export default PhoneVerificationConfirmScreen;
